import { useCallback, useEffect, useMemo, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { moderatorActionsStatsProxy } from './apiGateway';

type PeriodMode = 'month' | 'custom';

type ModeratorOption = {
    id: string;
    login: string;
    fullName: string;
    role?: string;
};

type PeriodPayload = {
    mode: PeriodMode;
    month: string | null;
    dateFrom: string | null;
    dateTo: string | null;
    startDate: string;
    endDate: string;
};

type SummaryItem = {
    month: string;
    monthLabel: string;
    totalCount: number;
    adFirstModerationCount: number;
    reviewModerationCount: number;
    complaintWorkCount: number;
    searchCount: number;
    searchEntityActionCount: number;
};

type ActionItem = {
    id: string;
    actionType: string;
    actionLabel: string;
    createdAt: string | null;
    entity: {
        id: string;
        type: string;
    };
    result: {
        code: string;
        label: string;
    };
    metadata: Record<string, unknown> | null;
};

type StatisticsResponse = {
    period: PeriodPayload;
    moderator: {
        id: string;
        login: string;
        fullName: string;
    };
    summaryByMonth: SummaryItem[];
    items: ActionItem[];
    pagination: {
        limit: number;
        hasNextPage: boolean;
        nextCursor: string | null;
    };
};

type ModeratorActionsStatisticsPageProps = {
    mode: 'personal' | 'moderators';
};

const PAGE_LIMIT = 50;

function toMonthKey(date: Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function resolveMonthRange(monthKey: string) {
    const match = monthKey.match(/^(\d{4})-(0[1-9]|1[0-2])$/);
    if (!match) {
        const fallbackDate = new Date();
        const fallbackKey = toMonthKey(fallbackDate);
        return resolveMonthRange(fallbackKey);
    }

    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    const start = new Date(year, monthIndex, 1, 12, 0, 0, 0);
    const end = new Date(year, monthIndex + 1, 0, 12, 0, 0, 0);

    const dateToString = (value: Date) => {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, '0');
        const d = String(value.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    return {
        start: dateToString(start),
        end: dateToString(end)
    };
}

function buildMonthOptions(monthsCount = 12) {
    const now = new Date();
    return Array.from({ length: monthsCount }, (_, index) => {
        const date = new Date(now.getFullYear(), now.getMonth() - index, 1, 12, 0, 0, 0);
        const value = toMonthKey(date);
        return {
            value,
            label: date.toLocaleString('ru-RU', { month: 'long', year: 'numeric' })
        };
    });
}

function formatDateTime(value: string | null | undefined) {
    if (!value) {
        return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '-';
    }
    return date.toLocaleString('ru-RU');
}

function formatEntityLabel(type: string, id: string) {
    const normalizedType = String(type || '').trim().toLowerCase();
    if (normalizedType === 'ad') {
        return `Объявление: ${id || '-'}`;
    }
    if (normalizedType === 'review') {
        return `Отзыв: ${id || '-'}`;
    }
    if (normalizedType === 'complaint') {
        return `Жалоба: ${id || '-'}`;
    }
    if (normalizedType === 'user') {
        return `Пользователь: ${id || '-'}`;
    }
    return id || '-';
}

function formatMetadata(value: Record<string, unknown> | null) {
    if (!value || typeof value !== 'object') {
        return '-';
    }

    const details: string[] = [];
    const operationType = typeof value.operationType === 'string' ? value.operationType.trim().toUpperCase() : '';
    const bonusesValue = Number(value.bonuses);
    const publicReason = typeof value.publicReason === 'string' ? value.publicReason.trim() : '';
    const youTrackIssueUrl = typeof value.youTrackIssueUrl === 'string' ? value.youTrackIssueUrl.trim() : '';
    const hasComment = value.hasComment === true;
    const skipExpertModeration = value.skipExpertModeration === true;
    const avatarId = typeof value.avatarId === 'string' ? value.avatarId.trim() : '';

    if (operationType === 'ACCRUAL') {
        details.push('Операция: начисление');
    } else if (operationType === 'WRITE_OFF') {
        details.push('Операция: списание');
    }
    if (Number.isFinite(bonusesValue) && bonusesValue > 0) {
        details.push(`Бонусы: ${Math.trunc(bonusesValue)}`);
    }
    if (publicReason) {
        details.push(`Причина: ${publicReason}`);
    }
    if (youTrackIssueUrl) {
        details.push(`YouTrack: ${youTrackIssueUrl}`);
    }
    if (hasComment) {
        details.push('Есть комментарий');
    }
    if (skipExpertModeration) {
        details.push('Опубликовано без эксперта');
    }
    if (avatarId) {
        details.push(`ID фото: ${avatarId}`);
    }

    return details.length ? details.join(' · ') : '-';
}

function formatModeratorLabel(moderator: ModeratorOption | null) {
    if (!moderator) {
        return '-';
    }
    const fullName = moderator.fullName.trim();
    const login = moderator.login.trim();

    const roleLabel = moderator.role?.trim() || 'moderator';
    if (fullName && login) {
        return `${fullName} (${login}) [${roleLabel}]`;
    }

    return `${fullName || login || moderator.id} [${roleLabel}]`;
}

const ModeratorActionsStatisticsPage = ({ mode }: ModeratorActionsStatisticsPageProps) => {
    const notify = useNotify();

    const initialMonth = toMonthKey(new Date());
    const initialMonthRange = resolveMonthRange(initialMonth);

    const [periodMode, setPeriodMode] = useState<PeriodMode>('month');
    const [selectedMonth, setSelectedMonth] = useState(initialMonth);
    const [dateFrom, setDateFrom] = useState(initialMonthRange.start);
    const [dateTo, setDateTo] = useState(initialMonthRange.end);

    const [moderators, setModerators] = useState<ModeratorOption[]>([]);
    const [moderatorsLoading, setModeratorsLoading] = useState(false);
    const [selectedModeratorId, setSelectedModeratorId] = useState('');

    const [period, setPeriod] = useState<PeriodPayload | null>(null);
    const [selectedModerator, setSelectedModerator] = useState<ModeratorOption | null>(null);
    const [summaryByMonth, setSummaryByMonth] = useState<SummaryItem[]>([]);
    const [items, setItems] = useState<ActionItem[]>([]);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [hasNextPage, setHasNextPage] = useState(false);

    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const monthOptions = useMemo(() => buildMonthOptions(), []);

    const buildParams = useCallback((cursor?: string | null) => {
        const params: {
            month?: string;
            dateFrom?: string;
            dateTo?: string;
            cursor?: string;
            limit: number;
        } = {
            limit: PAGE_LIMIT
        };

        if (periodMode === 'month') {
            params.month = selectedMonth;
        } else {
            params.dateFrom = dateFrom;
            params.dateTo = dateTo;
        }

        if (cursor) {
            params.cursor = cursor;
        }

        return params;
    }, [periodMode, selectedMonth, dateFrom, dateTo]);

    const loadStats = useCallback(async (reset: boolean) => {
        if (mode === 'moderators' && !selectedModeratorId) {
            return;
        }

        if (reset) {
            setLoading(true);
            setError(null);
        } else {
            setLoadingMore(true);
        }

        try {
            const params = buildParams(reset ? null : nextCursor);
            const response = mode === 'personal'
                ? await moderatorActionsStatsProxy.getPersonalStats(params)
                : await moderatorActionsStatsProxy.getModeratorStats(selectedModeratorId, params);

            const payload = response as StatisticsResponse;
            const payloadItems = Array.isArray(payload?.items) ? payload.items : [];

            setPeriod(payload?.period ?? null);
            setSummaryByMonth(Array.isArray(payload?.summaryByMonth) ? payload.summaryByMonth : []);
            setSelectedModerator(payload?.moderator ?? null);
            setItems((currentItems) => (reset ? payloadItems : [...currentItems, ...payloadItems]));
            setHasNextPage(Boolean(payload?.pagination?.hasNextPage));
            setNextCursor(payload?.pagination?.nextCursor ?? null);
        } catch (loadError) {
            console.error('Failed to load moderator actions stats:', loadError);
            setError('Не удалось загрузить статистику');
            notify('Не удалось загрузить статистику', { type: 'error' });
            if (reset) {
                setItems([]);
                setSummaryByMonth([]);
                setHasNextPage(false);
                setNextCursor(null);
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [mode, selectedModeratorId, buildParams, nextCursor, notify]);

    const loadModerators = useCallback(async () => {
        if (mode !== 'moderators') {
            return;
        }

        setModeratorsLoading(true);
        try {
            const response = await moderatorActionsStatsProxy.getModerators();
            const list = Array.isArray(response?.data) ? response.data as ModeratorOption[] : [];
            setModerators(list);
            setSelectedModeratorId((currentId) => {
                if (currentId && list.some((moderator) => moderator.id === currentId)) {
                    return currentId;
                }
                return list[0]?.id || '';
            });
            if (list.length === 0) {
                setLoading(false);
                setItems([]);
                setSummaryByMonth([]);
                setHasNextPage(false);
                setNextCursor(null);
            }
        } catch (loadError) {
            console.error('Failed to load moderators list:', loadError);
            notify('Не удалось загрузить список сотрудников', { type: 'error' });
            setModerators([]);
            setSelectedModeratorId('');
            setLoading(false);
        } finally {
            setModeratorsLoading(false);
        }
    }, [mode, notify]);

    useEffect(() => {
        if (periodMode !== 'month') {
            return;
        }
        const range = resolveMonthRange(selectedMonth);
        setDateFrom(range.start);
        setDateTo(range.end);
    }, [periodMode, selectedMonth]);

    useEffect(() => {
        if (mode === 'moderators') {
            void loadModerators();
            return;
        }

        void loadStats(true);
    }, [mode, loadModerators]);

    useEffect(() => {
        if (mode !== 'moderators') {
            return;
        }
        if (!selectedModeratorId) {
            return;
        }

        void loadStats(true);
    }, [mode, selectedModeratorId]);

    const pageTitle = mode === 'personal' ? 'Моя статистика модератора' : 'Статистика команды';

    const handleApplyFilters = () => {
        void loadStats(true);
    };

    const handleLoadMore = () => {
        if (!hasNextPage || !nextCursor || loadingMore) {
            return;
        }
        void loadStats(false);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Title title={pageTitle} />

            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
                {mode === 'moderators' ? (
                    <FormControl size="small" sx={{ minWidth: 320 }} disabled={moderatorsLoading}>
                        <InputLabel id="moderator-stats-moderator-label">Сотрудник</InputLabel>
                        <Select
                            labelId="moderator-stats-moderator-label"
                            value={selectedModeratorId}
                            label="Сотрудник"
                            onChange={(event) => setSelectedModeratorId(String(event.target.value))}
                        >
                            {moderators.map((moderator) => (
                                <MenuItem key={moderator.id} value={moderator.id}>
                                    {formatModeratorLabel(moderator)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : null}

                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="moderator-stats-period-mode-label">Фильтр периода</InputLabel>
                    <Select
                        labelId="moderator-stats-period-mode-label"
                        value={periodMode}
                        label="Фильтр периода"
                        onChange={(event) => setPeriodMode(String(event.target.value) as PeriodMode)}
                    >
                        <MenuItem value="month">По месяцу</MenuItem>
                        <MenuItem value="custom">Кастомный период</MenuItem>
                    </Select>
                </FormControl>

                {periodMode === 'month' ? (
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="moderator-stats-month-label">Месяц</InputLabel>
                        <Select
                            labelId="moderator-stats-month-label"
                            value={selectedMonth}
                            label="Месяц"
                            onChange={(event) => setSelectedMonth(String(event.target.value))}
                        >
                            {monthOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    <>
                        <TextField
                            label="Дата с"
                            type="date"
                            size="small"
                            value={dateFrom}
                            onChange={(event) => setDateFrom(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Дата по"
                            type="date"
                            size="small"
                            value={dateTo}
                            onChange={(event) => setDateTo(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </>
                )}

                <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    disabled={loading || moderatorsLoading}
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0, minWidth: 110 }}
                >
                    Применить
                </Button>
            </Stack>

            {period ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Период: {formatDateTime(period.startDate)} - {formatDateTime(period.endDate)}
                    {selectedModerator ? ` · Сотрудник: ${formatModeratorLabel(selectedModerator)}` : ''}
                </Typography>
            ) : null}

            {loading ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <CircularProgress size={20} />
                    <Typography>Загрузка статистики...</Typography>
                </Stack>
            ) : null}

            {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}

            {mode === 'moderators' && !moderatorsLoading && moderators.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Нет пользователей для выбранной статистики
                </Alert>
            ) : null}

            {!loading ? (
                <>
                    <Card sx={{ borderRadius: 2, mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Сводка по месяцам
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Месяц</TableCell>
                                            <TableCell align="right">Всего действий</TableCell>
                                            <TableCell align="right">Объявления</TableCell>
                                            <TableCell align="right">Отзывы</TableCell>
                                            <TableCell align="right">Жалобы</TableCell>
                                            <TableCell align="right">Поиск</TableCell>
                                            <TableCell align="right">Действия из поиска</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {summaryByMonth.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center">Нет данных за выбранный период</TableCell>
                                            </TableRow>
                                        ) : summaryByMonth.map((summary) => (
                                            <TableRow key={summary.month}>
                                                <TableCell>{summary.monthLabel}</TableCell>
                                                <TableCell align="right">{summary.totalCount}</TableCell>
                                                <TableCell align="right">{summary.adFirstModerationCount}</TableCell>
                                                <TableCell align="right">{summary.reviewModerationCount}</TableCell>
                                                <TableCell align="right">{summary.complaintWorkCount}</TableCell>
                                                <TableCell align="right">{summary.searchCount}</TableCell>
                                                <TableCell align="right">{summary.searchEntityActionCount}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>

                    <Card sx={{ borderRadius: 2 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Операции пользователя
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                Пагинация: по {PAGE_LIMIT} элементов
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Дата</TableCell>
                                            <TableCell>Операция</TableCell>
                                            <TableCell>Сущность</TableCell>
                                            <TableCell>Результат</TableCell>
                                            <TableCell>Детали</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">Нет операций за выбранный период</TableCell>
                                            </TableRow>
                                        ) : items.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                                                <TableCell>{item.actionLabel || '-'}</TableCell>
                                                <TableCell>{formatEntityLabel(item.entity?.type, item.entity?.id)}</TableCell>
                                                <TableCell>{item.result?.label || item.result?.code || '-'}</TableCell>
                                                <TableCell>{formatMetadata(item.metadata)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {hasNextPage ? (
                                <Box sx={{ mt: 2 }}>
                                    <Button variant="outlined" onClick={handleLoadMore} disabled={loadingMore}>
                                        {loadingMore ? 'Загрузка...' : 'Показать еще'}
                                    </Button>
                                </Box>
                            ) : null}
                        </CardContent>
                    </Card>
                </>
            ) : null}
        </Box>
    );
};

export const ModeratorPersonalStatisticsPage = () => (
    <ModeratorActionsStatisticsPage mode="personal" />
);

export const ModeratorsStatisticsPage = () => (
    <ModeratorActionsStatisticsPage mode="moderators" />
);
